import React from 'react';
import { Box, Flex, Input, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { MdArrowDropDown } from 'react-icons/md';

export type CsSelectOption = {
  value: string;
  label: string;
};

export type CsSelectProps = {
  label?: string;
  value: string;
  options: CsSelectOption[];
  onChange: (value: string) => void;

  pillBg?: string;
  pillBorderColor?: string;
  pillShadow?: string;
  textColor?: string;

  circleBg?: string;
  circleBorderColor?: string;
  iconColor?: string;

  height?: string | number;
  minWidth?: string | number;
  circleSize?: string | number;

  isDisabled?: boolean;
  isSearchable?: boolean;
  searchPlaceholder?: string;
};

export function CsSelect({
  label,
  value,
  options,
  onChange,
  pillBg = 'gray.100',
  pillBorderColor = 'gray.100',
  pillShadow = 'sm',
  textColor = 'black',
  circleBg = 'white',
  circleBorderColor = 'blue.500',
  iconColor = 'blue.500',
  height = '40px',
  minWidth = '220px',
  circleSize = '35px',
  isDisabled = false,
  isSearchable = false,
  searchPlaceholder = 'Pesquisar...',
}: CsSelectProps) {
  const [search, setSearch] = React.useState('');
  const selected = options.find((o) => o.value === value) ?? options[0];
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredOptions = normalizedSearch
    ? options.filter((option) => option.label.toLocaleLowerCase().includes(normalizedSearch))
    : options;

  return (
    <Flex align="center" gap={4} w="100%">
      {label ? (
        <Text fontSize="2xl" color="gray.500" lineHeight="1" whiteSpace="nowrap">
          {label}
        </Text>
      ) : null}

      <Menu placement="bottom-end" onClose={() => setSearch('')}>
        {({ isOpen }) => (
          <>
            <MenuButton
              as="button"
              type="button"
              disabled={isDisabled}
              style={{ background: 'transparent', padding: 0, border: 0, width: '100%' }}
            >
              <Flex
                align="center"
                justify="space-between"
                bg={pillBg}
                border="1px solid"
                borderColor={pillBorderColor}
                borderRadius="999px"
                h={height}
                w="100%"
                pl="24px"
                boxShadow={pillShadow}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                opacity={isDisabled ? 0.6 : 1}
              >
                <Text fontSize="md" fontWeight="500" color={textColor} lineHeight="1">
                  {selected?.label ?? value}
                </Text>

                <Flex
                  align="center"
                  justify="center"
                  w={circleSize}
                  h={circleSize}
                  bg={circleBg}
                  borderRadius="full"
                  border="1px solid"
                  borderColor={circleBorderColor}
                  boxShadow="md"
                  transition="transform 0.15s ease"
                  transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                >
                  <Box color={iconColor} transform="translateY(1px)">
                    <MdArrowDropDown size={28} />
                  </Box>
                </Flex>
              </Flex>
            </MenuButton>

            <MenuList
              borderRadius="14px"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="md"
              py={2}
              minW={minWidth}
              w="100%"
              maxH="320px"
              overflowY="auto"
              sx={{ overscrollBehavior: 'contain' }}
            >
              {isSearchable && (
                <Box px={2} pb={2} position="sticky" top={0} bg="white" zIndex={1}>
                  <Input
                    autoFocus
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Escape') event.stopPropagation();
                    }}
                    placeholder={searchPlaceholder}
                    borderRadius="10px"
                    size="sm"
                  />
                </Box>
              )}

              {filteredOptions.map((opt) => (
                <MenuItem
                  key={opt.value}
                  onClick={() => onChange(opt.value)}
                  fontSize="md"
                  fontWeight={opt.value === value ? '700' : '500'}
                  _hover={{ bg: 'gray.100' }}
                  _focus={{ bg: 'gray.100' }}
                  mx={0}
                  borderRadius="10px"
                >
                  {opt.label}
                </MenuItem>
              ))}

              {!filteredOptions.length && (
                <Text px={4} py={2} color="gray.500" fontSize="sm">
                  Nenhum modelo encontrado
                </Text>
              )}
            </MenuList>
          </>
        )}
      </Menu>
    </Flex>
  );
}

export default CsSelect;
